import React, {useEffect, useCallback, useState} from 'react';
import {batch} from 'react-redux';
import {ActionFunction1, Action} from 'redux-actions';
import {curry} from 'ramda';
import wrap from 'wraptile';
import getSelection from '@pluto/get-selection';
import {
    ApTimelineMarker,
    ApTimelineMetadata,
    MarkerApi,
} from '@accurate-player/accurate-player-components-external';
import {ifApTimelineMetadata, ifApTimelineMarker} from '@pluto/player-check/player-check';

import {OnBooleanSetState} from '@pluto/@types';
import {Item} from 'VideoAudioQC/QCMedia/Redux/QCMediaActions';
import {SetActiveTab} from '../../QCTabs/Redux/QCTabsActions';
import {TabIndex} from '../../QCTabs/QCTabsTypes';
import {
    OnTimelineMarkerClick,
    TimelineRow,
    TimelineMarker,
    MouseTouchEvent,
    OnTimelineMarkerEnter,
    OnTimelineMarkerLeave,
} from '../TimelineTypes';
import MarkerRow from './MarkerRow';
import {ToggleSelectedMarker, ToggleSelectedRow} from '../Redux/TimelineActions';
import {getMarkerStyling, ThemeMarkerType} from '../TimelineTheme';

type ResetMarkersAction = ActionFunction1<void, Action<void>>;

export interface MarkerRowContainerProps {
    url: string;
    item: Item;
    markerRowData: TimelineRow;
    setMarkers: (url: string, start: number, end: number) => any;
    resetMarkers: ResetMarkersAction;
    selectedRowId: string;
    selectedMarkerId: string;
    toggleSelectedMarker: ToggleSelectedMarker;
    toggleSelectedRow: ToggleSelectedRow;
    setActiveTab?: SetActiveTab;
    tabIndex?: TabIndex;
    markerType?: ThemeMarkerType;
}

export const dispatchClickActions = wrap(
    (props: MarkerRowContainerProps, marker: TimelineMarker) => {
        const {
            markerRowData,
            tabIndex,
            setActiveTab,
            toggleSelectedMarker,
            selectedMarkerId,
        } = props;
        const {identifier, type} = markerRowData;

        // Do not toggle tab when deselecting marker
        if (
            setActiveTab !== undefined &&
            tabIndex !== undefined &&
            selectedMarkerId !== marker.identifier
        )
            setActiveTab(tabIndex);

        toggleSelectedMarker(marker.identifier, identifier, type);
    }
);

export const handleMarkerClick = curry(
    (
        props: MarkerRowContainerProps,
        marker: TimelineMarker,
        api: MarkerApi,
        event: MouseTouchEvent
    ) => {
        event.stopPropagation();

        batch(dispatchClickActions(props, marker));
    }
);

export const handleMarkerMouseEnter = wrap((setHover: OnBooleanSetState) => {
    setHover(true);
});

export const handleMarkerMouseLeave = wrap((setHover: OnBooleanSetState) => {
    setHover(false);
});

export const onMarkerRowClicked = wrap(
    (markerRowData: TimelineRow, toggleSelectedRow: ToggleSelectedRow) => {
        const {identifier, type} = markerRowData;

        toggleSelectedRow(identifier, type);
    }
);

type MarkerEventsProps = {
    onMarkerClick: OnTimelineMarkerClick;
    onMarkerMouseEnter: OnTimelineMarkerEnter;
    onMarkerMouseLeave: OnTimelineMarkerLeave;
};

export const updateMarkerRowContainer = ifApTimelineMetadata(
    (
        markerEventsProps: MarkerEventsProps,
        props: MarkerRowContainerProps,
        node: ApTimelineMetadata
    ) => {
        const {markerRowData, selectedMarkerId, selectedRowId, markerType} = props;
        const {onMarkerClick, onMarkerMouseEnter, onMarkerMouseLeave} = markerEventsProps;

        node.apMarkerClick = onMarkerClick;
        node.apMarkerMouseEnter = onMarkerMouseEnter;
        node.apMarkerMouseLeave = onMarkerMouseLeave;
        node.selection = getSelection(markerRowData, selectedMarkerId, selectedRowId);
        node.markerStyle = getMarkerStyling(markerType ?? ThemeMarkerType.Normal, true);
    }
);

export const handleMarkerRowContainerChange = curry(
    (
        markerEventsProps: MarkerEventsProps,
        props: MarkerRowContainerProps,
        node: ApTimelineMetadata
    ) => {
        updateMarkerRowContainer(node, markerEventsProps, props, node);
    }
);

const useMarkerRowContainerRef = (
    props: MarkerRowContainerProps,
    setHover: OnBooleanSetState
) => {
    const onMarkerClick = handleMarkerClick(props);
    const onMarkerMouseEnter = handleMarkerMouseEnter(setHover);
    const onMarkerMouseLeave = handleMarkerMouseLeave(setHover);
    const markerEventsProps: MarkerEventsProps = {
        onMarkerClick,
        onMarkerMouseEnter,
        onMarkerMouseLeave,
    };
    const onMarkerChange = handleMarkerRowContainerChange(markerEventsProps, props);
    const setRef = useCallback(onMarkerChange, [onMarkerClick]);

    return [setRef];
};

export const updateMarkerRow = ifApTimelineMarker(
    (markers: TimelineMarker[], node: ApTimelineMarker) => {
        node.data = markers;
    }
);

const handleMarkersChange = curry((markers: TimelineMarker[], node: ApTimelineMarker) => {
    updateMarkerRow(node, markers, node);
});

const useMarkerRowRef = (markers: TimelineMarker[]) => {
    const onMarkersChange = handleMarkersChange(markers);
    const setRef = useCallback(onMarkersChange, [markers]);

    return [setRef];
};

export const handleMount = wrap((props: MarkerRowContainerProps) => {
    const {url, item, setMarkers, resetMarkers} = props;
    const {duration} = item.video;

    setMarkers(url, 0, duration);

    return resetMarkers;
});

export default function MarkerRowContainer(props: MarkerRowContainerProps) {
    const {markerRowData, toggleSelectedRow, selectedRowId} = props;
    const {markers, label, identifier} = markerRowData;

    const [setMarkerRowRef] = useMarkerRowRef(markers);
    const [hover, setHover] = useState<boolean>(false);
    const [setMarkerRowContainerRef] = useMarkerRowContainerRef(props, setHover);
    const onMount = handleMount(props);
    useEffect(onMount, []);
    const handleMarkerRowClicked = onMarkerRowClicked(markerRowData, toggleSelectedRow);
    useEffect(handleMarkerRowClicked, []);

    return (
        <MarkerRow
            setMarkerRowRef={setMarkerRowRef}
            setMarkerRowContainerRef={setMarkerRowContainerRef}
            onRowClicked={handleMarkerRowClicked}
            hover={hover}
            rowLabel={label}
            rowId={identifier}
            selectedRowId={selectedRowId}
        />
    );
}
